/**
 * Sessions hook
 *
 * Manages session list and session selection.
 */

import { useCallback, useEffect, useRef } from 'react';
import { useUserStore } from '../stores/userStore';
import {
  getSessions,
  getSession,
  createSession,
  deleteSession,
} from '../api/sessions';

const SESSIONS_PAGE_SIZE = 20;

export function useSessions() {
  const abortControllerRef = useRef<AbortController | null>(null);
  const {
    sessions,
    currentSessionUuid,
    messages,
    isSessionLoading,
    isAuthenticated,
    hasMoreSessions,
    isLoadingMoreSessions,
    sessionsLoadedCount,
    setSessions,
    appendSessions,
    setLoadingMoreSessions,
    addSession,
    setCurrentSession,
    setMessages,
    setSessionLoading,
    updateSessionTitle,
    removeSession,
  } = useUserStore();

  /**
   * Load initial sessions on auth
   */
  useEffect(() => {
    if (!isAuthenticated) return;

    let mounted = true;

    async function loadInitialSessions() {
      try {
        const { sessions: sessionsData, hasMore } = await getSessions(
          SESSIONS_PAGE_SIZE,
          0
        );
        if (mounted) {
          setSessions(sessionsData, hasMore);
        }
      } catch (error) {
        console.error('Failed to load sessions:', error);
      }
    }

    loadInitialSessions();

    return () => {
      mounted = false;
    };
  }, [isAuthenticated, setSessions]);

  /**
   * Load more sessions for infinite scroll
   * Uses sessionsLoadedCount (not sessions.length) because local add/remove
   * would throw off the offset otherwise
   */
  const loadMoreSessions = useCallback(async () => {
    if (isLoadingMoreSessions || !hasMoreSessions) return;

    setLoadingMoreSessions(true);
    try {
      const { sessions: moreSessions, hasMore } = await getSessions(
        SESSIONS_PAGE_SIZE,
        sessionsLoadedCount
      );
      appendSessions(moreSessions, hasMore);
    } catch (error) {
      console.error('Failed to load more sessions:', error);
    } finally {
      setLoadingMoreSessions(false);
    }
  }, [
    isLoadingMoreSessions,
    hasMoreSessions,
    sessionsLoadedCount,
    setLoadingMoreSessions,
    appendSessions,
  ]);

  /**
   * Select a session and load its messages
   */
  const selectSession = useCallback(
    async (uuid: string) => {
      if (uuid === currentSessionUuid) return;

      // Cancel any pending request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      abortControllerRef.current = new AbortController();

      setSessionLoading(true);
      setCurrentSession(uuid);

      try {
        const { messages: sessionMessages } = await getSession(uuid);
        // Only update if this request wasn't aborted
        if (!abortControllerRef.current?.signal.aborted) {
          setMessages(sessionMessages);
        }
      } catch (error) {
        // Ignore abort errors
        if (error instanceof Error && error.name === 'AbortError') {
          return;
        }
        console.error('Failed to load session:', error);
        setMessages([]);
      } finally {
        if (!abortControllerRef.current?.signal.aborted) {
          setSessionLoading(false);
        }
      }
    },
    [currentSessionUuid, setCurrentSession, setMessages, setSessionLoading]
  );

  /**
   * Create a new session
   */
  const newSession = useCallback(async () => {
    try {
      const uuid = await createSession();
      const newSessionObj = {
        id: Date.now(),
        session_uuid: uuid,
        title: 'Nova sesija',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };

      addSession(newSessionObj);
      setCurrentSession(uuid);
      setMessages([]);

      return uuid;
    } catch (error) {
      console.error('Failed to create session:', error);
      throw error;
    }
  }, [addSession, setCurrentSession, setMessages]);

  /**
   * Delete a session
   */
  const removeSessionHandler = useCallback(
    async (uuid: string) => {
      try {
        await deleteSession(uuid);
        removeSession(uuid);
      } catch (error) {
        console.error('Failed to delete session:', error);
        throw error;
      }
    },
    [removeSession]
  );

  /**
   * Refresh sessions list (resets to first page)
   */
  const refreshSessions = useCallback(async () => {
    const { sessions: sessionsData, hasMore } = await getSessions(
      SESSIONS_PAGE_SIZE,
      0
    );
    setSessions(sessionsData, hasMore);
  }, [setSessions]);

  return {
    sessions,
    currentSessionUuid,
    messages,
    isSessionLoading,
    hasMoreSessions,
    isLoadingMoreSessions,
    selectSession,
    newSession,
    deleteSession: removeSessionHandler,
    refreshSessions,
    loadMoreSessions,
    updateSessionTitle,
    setMessages,
  };
}

export default useSessions;
